import { Component, ViewChild, Input } from '@angular/core';
import { trigger, style, animate, transition } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, map, Observable, of, Subject } from 'rxjs';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { EventosService } from 'src/app/services/eventos.service';
import { CreateEventComponent } from '../../create-event/create-event.component';
import { TicketsService } from 'src/app/services/tickets.service';
import { SessionsService } from 'src/app/services/sessions.service';
import { CreateEventService } from 'src/app/services/create-event.service';
import { InfoCardComponent } from 'src/app/components/shared/info-card/info-card.component';
import { ComponentCanDeactivate } from 'src/app/guards/unsaved-event-changes.guard';
import { AppSettings } from 'src/app/app-settings';
import { formatDate } from '@angular/common';
import { end } from '@popperjs/core';

@Component({
  selector: 'app-router-create',
  templateUrl: './router-create.component.html',
  styleUrls: ['./router-create.component.scss'],
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ opacity: 0, height: 0, overflow: 'hidden', transform: 'translateY(-6px)' }),
        animate('220ms ease-out', style({ opacity: 1, height: '*', transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('180ms ease-in', style({ opacity: 0, height: 0, transform: 'translateY(-6px)' }))
      ])
    ]),
    // Aparición sutil del botón "Guardar cambios" cuando hay cambios sin guardar.
    trigger('popIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.85) translateY(4px)' }),
        animate('220ms cubic-bezier(0.34, 1.56, 0.64, 1)', style({ opacity: 1, transform: 'scale(1) translateY(0)' }))
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ opacity: 0, transform: 'scale(0.85)' }))
      ])
    ])
  ]
})
export class RouterCreateComponent implements ComponentCanDeactivate {

  event!: any;
  profile!: any;
  title!: any;
  nameTab: string = 'Información del evento';
  numberTab: number = 1;
  isDraftEvent: boolean = false;
  activePublish: boolean = false;
  mapPage: boolean = false;
  actionMap!: any;
  idEvent!: number;
  mapVersion!: any;
  embedded: boolean = false;
  // Controla el renderizado de <app-create-event>: en modo edición debe esperar a que
  // getData() rellene createEventService antes de montar el formulario (que solo lee
  // ese estado una vez, al iniciar).
  eventDataLoaded: boolean = true;

  // Snapshots inmutables de los datos del evento tal y como se cargaron desde el API,
  // usados en saveEvent() para mandar en el PUT solo los campos que realmente cambiaron
  // (ver stripUnchangedFields). Se rellenan en getData() y nunca se mutan después.
  originalDataCaptured: boolean = false;
  originalInfoValues: any = undefined;
  originalConfigTickets: any = undefined;
  originalTickets: any = undefined;
  originalCapacity: any = undefined;

  payload: any = {};

  @ViewChild(CreateEventComponent) createEvent!: CreateEventComponent;

  step!: any;

  // Datos evento
  configTickets!: any;
  tickets!: any;
  ticketsApi!: any;
  venue!: any;
  useMapVenue: boolean = false;

  savedEvent: boolean = false;

  // Se acaba de publicar el evento y la navegación a "Eventos" es la salida prevista
  // del propio flujo, no un abandono del usuario: canDeactivate no debe preguntar nada.
  eventPublished: boolean = false;

  // Validators tab
  validInfo: boolean = false;
  validSessions: boolean = false;
  validTickets: boolean = false;
  validDiscounts: boolean = false;
  validCapacity: boolean = false;

  // Getter para verificar si todas las condiciones para publicar están cumplidas
  // (incluye TODOS los campos obligatorios, no solo los de los pasos con check verde)
  get canPublish(): boolean {
    return this.getMissingRequiredFields().length === 0;
  }

  // Evento ya publicado (en edición). En este caso no se muestra "Publicar evento"
  // y "Guardar cambios" mantiene el evento publicado (publica los cambios directamente).
  get isPublished(): boolean {
    return !!this.idEvent && !!this.event?.publish && !this.event?.draft;
  }

  // Entradas nominativas, para el badge de la cabecera de "Configuración de venta":
  // así se ve sin desplegar la sección, que es la única pista en edición (allí la
  // opción no se puede tocar y dentro solo queda un texto).
  // Se lee del servicio y no de this.configTickets, que solo se rellena al cargar
  // el evento y no reflejaría lo que se acaba de marcar durante el alta.
  get isNominativeEvent(): boolean {
    return !!this.ticketService.getTicketsConfig()?.nominative_tickets;
  }

  // Tipo de evento seleccionado: null = sin seleccionar
  // - 'normal': venta de entradas gestionada en Citylok
  // - 'informative': sin venta, solo informativo (badge "+INFO")
  // - 'external': venta gestionada fuera de Citylok, requiere URL externa
  eventType: 'informative' | 'external' | 'normal' | null = null;
  formCreated: boolean = false;

  selectEventType(type: 'informative' | 'external' | 'normal') {
    // Al reeditar un evento ya existente (en una sesión posterior) no se puede
    // cambiar el tipo de evento; durante el alta inicial sí, aunque ya se haya
    // guardado el borrador.
    if (this.embedded) {
      return;
    }
    this.eventType = type;
    this.formCreated = true;
    this.createEventService.patchEventType(type);
    this.openSections['event-type'] = false;
    this.openSections['info-event'] = true;
    this.openSections['sessions-event'] = true;
    this.openSections['ticket-event'] = true;
    this.openSections['map-event'] = true;
    this.openSections['sale-config-event'] = true;
    this.createEvent?.expandAll();
    this.validateSteps();
  }

  changeEventType(type: 'informative' | 'external' | 'normal') {
    this.eventType = type;
    // Parchear el form sin destruirlo
    this.createEventService.patchEventType(type);
    this.validateSteps();
  }

  // Estado de secciones expandidas/colapsadas
  openSections: {[key: string]: boolean} = {
    'event-type': true,
    'info-event': false,
    'sessions-event': false,
    'ticket-event': false,
    'map-event': false,
    'sale-config-event': false
  };

  toggleSection(slug: string) {
    // Al reeditar un evento ya existente, la sección "Tipo de evento" no se puede desplegar
    if (slug === 'event-type' && this.embedded) {
      return;
    }
    this.openSections[slug] = !this.openSections[slug];
  }

  // Paso actual (valor de :step en la ruta). En modo embebido (edición desde el
  // panel) solo se muestra la sección que corresponde a este valor, en vez de
  // todo el acordeón como en la creación.
  currentStep: string = null;

  // Slugs del menú de edición que viven dentro de <app-create-event> (una única
  // tarjeta "info-event" del acordeón, con 4 sub-tarjetas internas).
  private innerCardSlugs: { [slug: string]: string } = {
    'info-event': 'info',
    'lugar-event': 'lugar',
    'config-event': 'config',
    'multimedia-event': 'multimedia',
  };

  get isInfoGroupSlug(): boolean {
    return !!this.innerCardSlugs[this.currentStep];
  }

  // ¿Debe mostrarse esta sección? En creación (no embebido) se muestran todas;
  // en edición (embebido) solo la que coincide con el paso actual del menú.
  isSectionVisible(slug: string): boolean {
    if (!this.embedded) return true;
    if (slug === 'info-event') return this.isInfoGroupSlug;
    return this.currentStep === slug;
  }

  // ¿Debe mostrarse el contenido (no solo el título) de la sección? En edición,
  // al ser la única visible, se muestra siempre desplegada.
  isSectionOpen(slug: string): boolean {
    if (this.embedded) return this.isSectionVisible(slug);
    return !!this.openSections[slug];
  }

  // Tarjeta interna de app-create-event a mostrar en modo edición (null = todas,
  // comportamiento normal de creación).
  get onlyCard(): string | null {
    if (!this.embedded) return null;
    return this.innerCardSlugs[this.currentStep] || null;
  }

  // Id tickets
  numTickets: number = 0;
  loadingTickets: boolean = false;

  // Guardado en curso: bloquea la pantalla con un overlay y descarta clicks
  // repetidos en "Guardar cambios" / "Publicar evento". Publicar puede tardar
  // varios segundos porque el backend genera las reservas de los abonados de
  // temporada (SeasonPassEventBookingService), y sin este bloqueo el usuario
  // creía que no había pasado nada, volvía a pulsar y se creaban eventos
  // duplicados.
  savingEvent: boolean = false;
  savingMessage: string = '';

  constructor(private activatedRoute: ActivatedRoute, private router: Router, private ticketService: TicketsService,
    private auth: AuthenticationService, private apiEvents: EventosService, private dialog: MatDialog,
    private createEventService: CreateEventService, private sessionsService: SessionsService) { }

  ngOnInit(): void {
    this.profile = this.auth.getProfile();
    this.embedded = location.pathname.includes('/detail/');
    this.step = this.apiEvents.step.pipe();
    let url = location.href;
    let slugUrl = url.split('/');
    let index = slugUrl.indexOf('new-event');
    let step = slugUrl[index + 1];
    this.apiEvents.setStepCreate(step);
    // this.tickets = this.ticketService.getTicketsSaved();
    // if (!this.tickets) {
    //   this.getTickets();
    // }

    this.activatedRoute.params.subscribe({
      next: (params) => {
        if (params.step) {
          this.currentStep = params.step;
          this.apiEvents.setStepCreate(params.step);
          // Cambiar titulo de navegador
          if (params.idEvent) {
            const newIdEvent = +params.idEvent;
            // Evitar recargar (y volver a mostrar el formulario vacío un instante)
            // al cambiar de sección dentro del mismo evento, ya que :idEvent no
            // cambia entre pasos del menú de edición.
            if (newIdEvent !== this.idEvent || !this.eventDataLoaded) {
              this.idEvent = newIdEvent;
              this.eventDataLoaded = false;
              this.getData();
            }
          }
          this.tickets = this.ticketService.getTicketsSaved();
          if (!this.tickets) {
            this.getTickets();
          }
          // Validar pasos al cambiar de ruta
          this.validateSteps();
        } else {
          this.apiEvents.setStepCreate(step);
        }
      }
    });

    // Validar pasos al iniciar
    this.validateSteps();

    // Suscribirse a cambios en tickets para revalidar automáticamente
    this.ticketService.tickets.subscribe({
      next: (tickets) => {
        if (tickets !== null) {
          this.validateSteps();
        }
      }
    });

    // Suscribirse a cambios en capacity para revalidar automáticamente
    this.createEventService.capacity.subscribe({
      next: (capacity) => {
        if (capacity !== null) {
          this.validateSteps();
        }
      }
    });

    // Suscribirse a cambios en las sesiones para revalidar automáticamente
    this.sessionsService.sessions.subscribe({
      next: (sessions) => {
        if (sessions !== null) {
          this.validateSteps();
        }
      }
    });

    // Suscribirse a cambios en el formulario de información para revalidar automáticamente
    this.createEventService.info.subscribe({
      next: (info) => {
        if (info !== null) {
          this.validateSteps();
        }
      }
    });
  }

  changePlace(event: any) {
    let objPlace = event;
    this.venue = objPlace.space;
    this.mapVersion = this.venue?.current_version;

    this.useMapVenue = objPlace?.useMapVenue ?? false;
    if (this.venue) {
      this.getTickets();
    }
  }

  getData() {
    this.apiEvents.getEventById(this.idEvent).subscribe({
      next: (res) => {
        const data = res.data;
        this.title = data.title;
        this.event = data;
        this.configTickets = {
          start_sales: data.start_sales,
          end_sales: data.end_sales,
          assume_fees: data.assume_fees,
          need_validation: data.need_validation,
          // El tope se guarda en events.max_tickets_per_order, pero el formulario
          // de configuración de venta lo edita como max_tickets_per_user (que es
          // lo que significa: entradas máximas por usuario, ver PurchaseLimitService).
          // Con la clave del backend el patchValue del formulario lo ignoraba y el
          // campo salía vacío al editar un evento que sí tenía tope.
          max_tickets_per_user: data.max_tickets_per_order ?? null,
          nominative_tickets: data.nominative_tickets ?? false,
          publish_date: data.publish_date || null,
          publish: data.publish_date ? true : false,
        };
        this.originalConfigTickets = JSON.parse(JSON.stringify(this.configTickets));
        this.ticketService.setTicketsConfig(this.configTickets);
        this.originalCapacity = data.capacity ?? null;
        // Sembrar el aforo real del evento en el observable compartido: si no, en
        // eventos sin mapa de butacas, MapSeatsCreateComponent solo lo rellena a
        // partir de space?.capacity (el aforo del LOCAL, no el guardado en el
        // evento) o lo deja en null si el evento no tiene local asociado,
        // provocando un falso "cambios sin guardar" contra originalCapacity.
        this.createEventService.setCapacity(this.originalCapacity);
        this.venue = data.space;
        this.mapVersion = data.map_version;
        this.eventType = data.informative ? 'informative' : (data.external ? 'external' : 'normal');

        // Rellenar el formulario de "Información del evento" con los datos del evento existente.
        // Debe hacerse antes de que se muestre <app-create-event> (ver eventDataLoaded / *ngIf
        // en la plantilla), porque CreateEventComponent solo lee este estado una vez, al iniciar.
        const categories = data.categories || (data.category ? [data.category] : []);
        const extraImages = this.mapCarouselImages(data.extraImages || data.images);
        const cropped = data.image_url ? (AppSettings.MEDIA_URI + '/events_images/' + data.image_url) : null;

        const infoValues = {
          id_company: data.id_company,
          title: data.title,
          description: data.description,
          categories: categories,
          cropped: cropped,
          online: data.online,
          space: data.space,
          latitude: data.latitude,
          longitude: data.longitude,
          address: data.address,
          use_map: data.use_map,
          // Versión del mapa de butacas con la que se creó/publicó ESTE evento
          // (puede diferir de space.current_version si el espacio se ha vuelto a
          // editar después). MapSeatsCreateComponent debe usar esta al editar.
          map_version: data.map_version,
          id_city: data.id_city,
          id_province: data.id_province,
          city_name: data.city_name,
          province_name: data.province_name,
          country_name: data.country_name,
          external: data.external,
          external_url: data.external_url,
          only_tpv_sales: data.only_tpv_sales,
          hiddenEvent: data.hidden,
          informative: data.informative,
          private_event: data.private_event,
          private_event_code: data.private_event_code,
          external_billing: data.external_billing,
          invoice_business_cif: data.invoice_business_cif,
          invoice_business_name: data.invoice_business_name,
          invoice_business_address: data.invoice_business_address,
          external_fee_billing: data.external_fee_billing,
          fee_invoice_business_cif: data.fee_invoice_business_cif,
          fee_invoice_business_name: data.fee_invoice_business_name,
          useCustomPrice: data.custom_from_price != null,
          customFromPrice: data.custom_from_price ?? null,
          extraImages: extraImages,
          hashtags: data.hashtags || [],
          youtube_url: data.youtube_url,
          sessions: data.sessions || [],
        };

        // Snapshot inmutable usado por saveEvent()/stripUnchangedFields() para saber
        // qué campos cambió realmente el usuario respecto a los datos cargados.
        this.originalInfoValues = JSON.parse(JSON.stringify(infoValues));

        this.createEventService.setInfoDetail({
          valid: true,
          values: infoValues
        });

        // Cargar las entradas ya configuradas del evento (TicketsCreateComponent
        // se suscribe a ticketService.tickets, igual que hacía el editor antiguo
        // con getTicketsByEvent).
        this.ticketService.getTicketsByEvent(this.idEvent).subscribe({
          next: (ticketsRes) => {
            const ticketsData = ticketsRes.data || [];
            // Capturado antes de setTicketsVenue: en el mismo tick síncrono en que
            // los tickets se hacen visibles/editables, así no hay ventana en la que
            // una edición del usuario pueda adelantarse a este snapshot.
            this.originalTickets = JSON.parse(JSON.stringify(ticketsData));
            this.ticketService.setTicketsVenue(ticketsData);
          }
        });

        this.eventDataLoaded = true;
        this.originalDataCaptured = true;

        // Desplegar las secciones aplicables, igual que al elegir el tipo de evento,
        // para que al entrar a editar se vea el formulario ya relleno.
        this.openSections['event-type'] = false;
        this.openSections['info-event'] = true;
        this.openSections['sessions-event'] = true;
        if (this.eventType === 'normal') {
          this.openSections['ticket-event'] = true;
          this.openSections['sale-config-event'] = true;
        }
        this.createEvent?.expandAll();

        this.validateSteps();
      }
    });
  }

  // Las fotos del carrusel guardadas llegan como {id, url}, pero la miniatura del
  // formulario pinta `cropped` (que solo traen las recién recortadas), así que se
  // veían rotas al editar. El `id` es lo que viaja luego en carousel_images_keep
  // para que el backend sepa cuáles se conservan.
  private mapCarouselImages(images: any[] | null | undefined): any[] {
    return (images || []).map((img: any) => ({
      ...img,
      cropped: img.cropped || (img.url?.startsWith('http')
        ? img.url
        : AppSettings.MEDIA_URI + '/events_images/' + img.url),
    }));
  }

  getTickets() {
    this.loadingTickets = true;
    if (this.useMapVenue) {
      // Con mapa: cargar tickets del espacio
      this.tickets = [];
      this.ticketService.setTicketsVenue(this.tickets);
      this.loadingTickets = false;
    } else {
      // Sin mapa: restaurar tickets guardados del servicio
      const savedTickets = this.ticketService.getTicketsSaved();
      this.tickets = savedTickets || [];
      this.loadingTickets = false;
    }
  }

  getDiscountVenue() {
    // Falta API - No hay descuentos de momento

  }

  validateSteps() {
    // Validar información del evento
    let saved = this.createEventService.getInfoEvent();
    this.validInfo = saved?.valid;

    // Validar sesiones - verificar que existan sesiones guardadas (en el observable o en el evento guardado)
    let sessionsFromObservable = this.sessionsService.getSessionsSaved();
    let sessionsFromSaved = saved?.values?.sessions || [];
    let allSessions = sessionsFromObservable && sessionsFromObservable.length > 0
      ? sessionsFromObservable
      : sessionsFromSaved;
    this.validSessions = allSessions && allSessions.length > 0;

    // Validar tickets - verificar que realmente existan tickets guardados con precios
    let tickets = this.ticketService.getTicketsSaved();
    const isExternal = saved?.values?.external;
    const isInformative = saved?.values?.informative;

    // Para eventos con venta externa o informativos, el paso de entradas no es obligatorio
    if (isExternal || isInformative) {
      this.validTickets = true;
    } else {
      // Ignorar tickets base sin configurar/desactivados
      tickets = (tickets || []).filter((t: any) => !(t.base && t.enabled === false));
      this.validTickets = tickets && tickets.length > 0 && tickets.some((t: any) => {
        // Si tiene niveles (mapa de butacas), verificar que al menos un nivel tenga precio
        if (t.levels && t.levels.length > 0) {
          return t.levels.some((level: any) => level.price != null && level.price !== undefined);
        }

        if (t.ticket_levels && t.ticket_levels.length > 0) {
          return t.ticket_levels.some((level: any) => level.price != null && level.price !== undefined);
        }

        if (t.ticket_seats && t.ticket_seats.length > 0) {
          return t.ticket_seats.some((seat: any) => seat.price != null && seat.price !== undefined);
        }

        // Si no tiene niveles, verificar el precio directo
        return t.price != null && t.price !== undefined;
      });
    }

    // Validar aforo - obligatorio excepto para eventos externos, informativos o que usan mapa de butacas
    let capacity = this.createEventService.getCapacity();
    const usesMap = saved?.values?.use_map;

    // Si es externo, informativo o usa mapa de butacas (numerado), el aforo no es obligatorio
    // Si el espacio tiene mapa pero el evento usa entradas sin numerar, el aforo SÍ es obligatorio
    if (isExternal || isInformative || usesMap) {
      this.validCapacity = true;
    } else {
      // Para eventos normales sin numeración, el aforo es obligatorio
      this.validCapacity = capacity != null && capacity != undefined && capacity > 0;
    }
  }

  // Fuente única de verdad de los campos obligatorios para publicar.
  // La usan tanto canPublish (habilitar el botón) como saveEvent(true) (mensaje de error al hacer click).
  getMissingRequiredFields(): string[] {
    const saved = this.createEventService.getInfoEvent();
    const errors: string[] = [];

    // Campos siempre obligatorios
    if (!saved?.values?.title) {
      errors.push('el título');
    }
    if (!saved?.values?.categories?.length) {
      errors.push('la categoría');
    }
    // `file` solo se rellena al elegir/recortar una imagen nueva; en edición la imagen
    // ya existente se representa como `cropped` (URL), sin que haga falta re-subirla.
    if (!saved?.values?.file && !saved?.values?.cropped) {
      errors.push('la imagen');
    }
    if (!saved?.values?.description) {
      errors.push('la descripción');
    }

    const isInformative = !!saved?.values?.informative;
    const isExternal = !!saved?.values?.external;

    // Sesiones - obligatorias para todos los tipos de evento, incluida la venta externa
    let sessionsFromObservable = this.sessionsService.getSessionsSaved();
    if (!sessionsFromObservable || sessionsFromObservable.length === 0) {
      sessionsFromObservable = saved?.values?.sessions || [];
    }
    if (!sessionsFromObservable || sessionsFromObservable.length === 0) {
      errors.push('al menos una sesión');
    }

    // Si el evento NO es informativo y NO es externo
    if (!isInformative && !isExternal) {
      // Entradas - ignorar tickets base sin configurar/desactivados y exigir precio configurado
      const tickets = (this.ticketService.getTicketsSaved() || []).filter((t: any) => !(t.base && t.enabled === false));
      const hasValidTicket = tickets.length > 0 && tickets.some((t: any) => {
        if (t.levels && t.levels.length > 0) {
          return t.levels.some((level: any) => level.price != null && level.price !== undefined);
        }
        if (t.ticket_levels && t.ticket_levels.length > 0) {
          return t.ticket_levels.some((level: any) => level.price != null && level.price !== undefined);
        }
        if (t.ticket_seats && t.ticket_seats.length > 0) {
          return t.ticket_seats.some((seat: any) => seat.price != null && seat.price !== undefined);
        }
        return t.price != null && t.price !== undefined;
      });
      if (!hasValidTicket) {
        errors.push('al menos una entrada con precio configurado');
      }
    }

    // Si el evento es externo
    if (isExternal && !saved?.values?.external_url) {
      errors.push('la URL externa');
    }

    // Si el evento es privado
    if (saved?.values?.private_event) {
      const privateCode = (saved?.values?.private_event_code || '').trim();
      if (!privateCode) {
        errors.push('el código de acceso del evento privado');
      }
    }

    // Si se factura a nombre de otra empresa
    if (saved?.values?.external_billing) {
      if (!(saved?.values?.invoice_business_cif || '').trim()) {
        errors.push('el CIF de la empresa de facturación');
      }
      if (!(saved?.values?.invoice_business_name || '').trim()) {
        errors.push('el nombre de la empresa de facturación');
      }
    }

    // Si los gastos de gestión se atribuyen a otra empresa. Solo se exige a quien puede
    // editarlo: para el resto de roles los campos están ocultos y bloquearles la
    // publicación por ellos les dejaría sin salida.
    if (saved?.values?.external_fee_billing && this.auth.isSuperAdmin()) {
      if (!(saved?.values?.fee_invoice_business_cif || '').trim()) {
        errors.push('el CIF de la empresa de los gastos de gestión');
      }
      if (!(saved?.values?.fee_invoice_business_name || '').trim()) {
        errors.push('el nombre de la empresa de los gastos de gestión');
      }
    }

    // Requisitos relacionados con espacio/dirección (solo externo exime el lugar)
    if (!saved?.values?.space && !isExternal && !saved?.values?.address) {
      errors.push('el evento debe tener un espacio o una dirección');
    }

    // Validación de aforo (excepto informativo y externo)
    if (!isExternal && !isInformative) {
      const capacity = this.createEventService.getCapacity();
      const hasSpace = !!saved?.values?.space;
      const useMapEnabled = hasSpace && saved?.values?.use_map === true;
      if (!useMapEnabled && !capacity) {
        errors.push('la capacidad del evento');
      }
    }

    return errors;
  }

  // Construye el mensaje HTML de campos pendientes (lista con viñetas) mostrado
  // en el InfoCardComponent, reutilizado tanto para el guardado en borrador
  // como para la validación completa de publicación.
  private buildMissingFieldsMessage(intro: string, errors: string[]): string {
    let msg = `${intro}:<br><br><div style="padding-left: 0; margin: 16px auto; display: inline-block; text-align: left;">`;
    errors.forEach((err) => {
      // Capitalizar la primera letra
      const capitalizedErr = err.charAt(0).toUpperCase() + err.slice(1);
      msg += `<div style="margin-bottom: 8px; font-weight: bold; display: flex; align-items: flex-start;"><span style="color: var(--color-primary); font-weight: bold; margin-right: 8px; flex-shrink: 0;">•</span><span> ${capitalizedErr}.</span></div>`;
    });
    msg += '</div>';
    return msg;
  }

  changeMenuEvent(event: any) {
    this.nameTab = event.name;
    this.numberTab = event.tab;
    this.apiEvents.setStepCreate(event.slug);

    // En edición, cada sección vive en su propia URL (:step); navegar en vez de
    // simplemente abrir el acordeón, ya que solo se renderiza la sección activa.
    if (this.embedded && event.slug) {
      const base = location.pathname.replace(/\/[^/]+\/[^/]+\/?$/, '');
      this.router.navigate([base + '/' + event.slug, this.idEvent]);
      return;
    }

    const innerCard = this.innerCardSlugs[event.slug];
    if (innerCard) {
      // Estas 4 secciones viven dentro de la tarjeta "info-event" (app-create-event)
      this.openSections['info-event'] = true;
      this.createEvent?.openCard(innerCard);
    } else if (event.slug && this.openSections.hasOwnProperty(event.slug)) {
      // Abrir la sección de destino en lugar de navegar
      this.openSections[event.slug] = true;
    }

    if (event.slug) {
      setTimeout(() => {
        document.getElementById('section-' + event.slug)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    // Validar todos los pasos
    this.validateSteps();
  }

  changeActionMap(save: boolean) {
    this.apiEvents.saveMapAction(save);
  }

  openLinkEvent() {
    let saved = this.createEventService.getInfoEvent();
    if (saved?.valid && this.savedEvent) {
      let titulo = saved?.values?.title;
      let titleUrl = titulo.replace(/[^\w\s]/g, '-').replace(/\s+/g, '-').replace(/-{2,}/g, '-').replace(/-{3,}/g, '-');
      if (titleUrl.charAt(titleUrl.length - 1) === '-') titleUrl = titleUrl.slice(0, -1);
      const url = `${AppSettings.FRONT}/draft/event/${this.idEvent + '-' + titleUrl.toLowerCase()}`;
      window.open(url, '_blank');
    } else {
      let msg = 'Debes rellenar los campos obligatorios antes de previsualizar el evento.';
      if (saved?.valid && !this.savedEvent) {
        msg = 'Debes guardar antes de previsualizar el evento.';
      }
      this.openInfoCard(msg);
    }
  }

  saveEvent(publish: boolean = false) {
    // Un guardado ya en vuelo: ignorar el click en vez de lanzar otro POST/PUT.
    if (this.savingEvent) {
      return;
    }

    let saved = this.createEventService.getInfoEvent();
    // let payload = saved?.values;
    let payload: any = {};

    // Al editar un evento ya publicado, "Guardar cambios" (publish=false) debe
    // mantenerlo publicado: los cambios se publican directamente en vez de
    // despublicar el evento. Se valida igual que al publicar.
    const keepPublished = !publish && this.isPublished;

    // Mínimo exigido por el backend para guardar el evento como borrador (ver
    // StoreEventRequest): título, al menos una categoría y descripción.
    if (!publish) {
      const draftErrors: string[] = [];
      if (!saved?.values?.title?.trim()) {
        draftErrors.push('el título');
      }
      if (!saved?.values?.categories?.length) {
        draftErrors.push('la categoría');
      }
      if (!saved?.values?.description) {
        draftErrors.push('la descripción');
      }
      if (draftErrors.length > 0) {
        this.openInfoCard(this.buildMissingFieldsMessage('Para guardar el evento debes completar', draftErrors));
        return;
      }
    }

    // Campo opcional, pero si hay algo escrito debe ser un enlace real a un vídeo de
    // YouTube (ver CreateEventService.isValidYoutubeUrl). Se bloquea el guardado tanto
    // en borrador como al publicar: un enlace roto no tiene utilidad en ningún caso.
    if (!this.createEventService.isValidYoutubeUrl(saved?.values?.youtube_url)) {
      this.openInfoCard('La URL del vídeo de YouTube no tiene un formato válido. Usa un enlace de youtube.com o youtu.be.');
      return;
    }

    //Información basica
    payload.title = saved?.values?.title;
    payload.description = saved?.values?.description;
    payload.category_ids = (saved?.values?.categories || []).map((c: any) => c.id);
    payload.id_company = saved?.values?.id_company;

    // El carrusel viaja en dos piezas: las fotos nuevas en base64 y los ids de las que
    // ya estaban y se conservan. Sin la segunda, el backend solo sabía añadir y quitar
    // una foto en el panel no la borraba de ninguna parte.
    const extraImagesRaw = saved?.values?.extraImages || [];
    payload.carousel_images = extraImagesRaw.filter((img: any) => img.file).map((img: any) => img.file);
    payload.carousel_images_keep = extraImagesRaw.filter((img: any) => img.id).map((img: any) => img.id);

    // "file"/"extraFile" solo tienen valor cuando el usuario ha subido/recortado una
    // imagen nueva en esta sesión de edición (ver onFileChange en create-event.component.ts);
    // si no ha tocado la portada, estos campos vienen vacíos y no se envían, dejando la
    // imagen ya guardada en el servidor intacta. El backend soporta "file" tanto en
    // creación como en edición (ver EventRepository::update).
    const fileValue = saved?.values?.file;
    const extraFileValue = saved?.values?.extraFile;

    if (fileValue) {
      payload.file = fileValue;
    }

    if (extraFileValue) {
      payload.extra_file = extraFileValue;
    }

    //Lugar
    payload.id_space = saved?.values?.space?.id;
    payload.address = saved?.values?.space ? saved?.values?.space?.address : saved?.values?.address;
    payload.id_city = saved?.values?.space ? saved?.values?.space?.id_city : saved?.values?.id_city;
    payload.id_province = saved?.values?.space ? (saved?.values?.space?.id_province ?? null) : (saved?.values?.id_province ?? null);
    payload.city_name = saved?.values?.city_name ?? null;
    payload.province_name = saved?.values?.province_name ?? null;
    payload.country_name = saved?.values?.country_name ?? null;
    payload.latitude = saved?.values?.space ? null : saved?.values?.latitude;
    payload.longitude = saved?.values?.space ? null : saved?.values?.longitude;
    payload.use_map = saved?.values?.use_map || false;

    //Tipo de evento
    payload.informative = saved?.values?.informative;
    payload.external = saved?.values?.external;
    payload.external_url = saved?.values?.external ? saved?.values?.external_url : null;
    payload.private_event = saved?.values?.private_event;
    payload.private_event_code = saved?.values?.private_event ? saved?.values?.private_event_code : null;
    payload.only_tpv_sales = saved?.values?.only_tpv_sales;
    payload.online = saved?.values?.online;
    payload.hidden = saved?.values?.hiddenEvent;
    payload.custom_from_price = saved?.values?.useCustomPrice ? saved?.values?.customFromPrice : null;

    //Facturación
    payload.invoice_business_cif = saved?.values?.invoice_business_cif;
    payload.invoice_business_name = saved?.values?.invoice_business_name;
    payload.invoice_business_address = (saved?.values?.invoice_business_address || '').trim() || null;
    payload.external_billing = saved?.values?.external_billing;

    //Gastos de gestión
    payload.fee_invoice_business_cif = saved?.values?.fee_invoice_business_cif;
    payload.fee_invoice_business_name = saved?.values?.fee_invoice_business_name;
    payload.external_fee_billing = saved?.values?.external_fee_billing;

    //YouTube
    payload.youtube_url = saved?.values?.youtube_url || null;

    //Fechas - Obtener sesiones del observable
    let sessionsFromObservable = this.sessionsService.getSessionsSaved();

    if (!sessionsFromObservable || sessionsFromObservable.length === 0) {
      sessionsFromObservable = saved?.values?.sessions || [];
    }

    // Config tickets
    let configTickets = this.ticketService.getTicketsConfig();

    const sessionsPayload = this.buildSessionsPayload(sessionsFromObservable, configTickets);
    if (sessionsPayload) {
      payload.sessions = sessionsPayload.sessions;
      payload.start_date = sessionsPayload.start_date;
      if (sessionsPayload.end_date) {
        payload.end_date = sessionsPayload.end_date;
      }
    }

    if (configTickets) {
      payload.start_sales = configTickets.start_sales;
      payload.end_sales = configTickets.end_sales;
      payload.assume_fees = configTickets.assume_fees;
      payload.need_validation = configTickets.need_validation;
      // El formulario lo edita como max_tickets_per_user; la columna del evento se
      // llama max_tickets_per_order. Leerlo con el nombre de la columna dejaba el
      // valor fuera del payload y el tope no se guardaba en ninguna parte.
      payload.max_tickets_per_order = configTickets.max_tickets_per_user ?? null;
      payload.nominative_tickets = !!configTickets.nominative_tickets;
      payload.publish_date = configTickets.publish ? configTickets.publish_date : null;
    }

    // Tickets
    const isMapEvent = !!(saved?.values?.use_map);
    const ticketsPayload = this.buildTicketsPayload(this.ticketService.getTicketsSaved(), configTickets, isMapEvent);
    if (ticketsPayload) {
      payload.tickets = ticketsPayload;
    }

    // Capacity
    let capacity = this.createEventService.getCapacity();
    if (capacity) {
      payload.capacity = capacity;
    }
    // Map - Solo enviar si es un evento nuevo o si el mapa ha cambiado
    if (saved?.values.space && saved?.values.place?.mainMap && !this.idEvent) {
      payload.mapEvent = payload.place.mainMap;
    }

    let map = this.createEventService.getJsonMap();
    if (map && !this.idEvent) {
      // Crear copia del mapa para no modificar el original
      const hiddenSeatsCopy = map.hiddenSeats.map((element: any) => ({
        seats: element.objectsSeats.filter((value: any) => !value.level).length
      }));

      payload.mapEvent = {
        "name": map.name,
        "hiddenSeats": hiddenSeatsCopy,
        "capacity": map.capacity,
        "levels": map.levels,
      };
    }

    // Validaciones para publicar (misma fuente que canPublish, ver getMissingRequiredFields)
    if (publish || keepPublished) {
      const errors = this.getMissingRequiredFields();

      // Normalizar el código de acceso privado si viene con espacios
      if (saved?.values?.private_event && saved?.values?.private_event_code) {
        saved.values.private_event_code = saved.values.private_event_code.trim();
      }

      // Mostrar errores si existen
      if (errors.length > 0) {
        this.openInfoCard(this.buildMissingFieldsMessage('Para publicar el evento debes completar', errors));
        return;
      }
    }

    // payload.image_url = saved?.values?.cropped; //TODO: back meter imagenes
    const croppedAux = saved?.values?.cropped;

    if (this.idEvent) {
      payload.id = this.idEvent;
    }

    // Estado del evento (draft/publish). Solo se manda cuando esta acción lo decide
    // de verdad; en el resto de casos NO viaja en el payload y el back conserva el
    // estado que ya tenía (ambos son 'sometimes' en UpdateEventRequest).
    //
    // Nunca se deduce el flag de this.event: durante el alta de un evento nuevo
    // this.event está vacío (solo lo rellena getData(), que necesita el id en la
    // ruta), así que calcularlo desde ahí dejaba el evento en publish=false +
    // draft=false, un limbo invisible tanto en "Eventos" como en "Borradores".
    if (publish || keepPublished) {
      payload.draft = false;
      payload.publish = true;
    } else if (!this.idEvent) {
      // Alta: el primer guardado siempre crea un borrador.
      payload.draft = true;
      payload.publish = false;
    }

    // A partir de aquí ya no hay vuelta atrás: se lanza la petición, así que se
    // bloquea la pantalla hasta que responda el backend.
    this.startSaving(publish || keepPublished);

    // Si está guardado
    if (this.idEvent) {
      this.stripUnchangedFields(payload);
      this.apiEvents.putEventos(this.idEvent, payload).subscribe({
        next: (res) => {
          this.savingEvent = false;
          saved.values.cropped = croppedAux;
          this.idEvent = res.data.id;
          // this.event al día con lo que devuelve el back (isPublished, keepPublished e
          // inactiveEvent() leen de aquí, y en el alta getData() nunca se ejecuta).
          this.event = { ...(this.event || {}), ...res.data };
          this.afterSaveEvent(publish);
        }, error: (err) => {
          this.savingEvent = false;
          saved.values.cropped = croppedAux;

          // Mensaje específico para error de URL externa no válida
          if (err?.status === 422 && err?.error?.errors?.external_url?.length) {
            this.openInfoCard('La URL externa no es válida. Revisa que tenga el formato correcto (por ejemplo: https://www.ejemplo.com).');
          } else if (err?.status === 422 && err?.error?.errors?.private_event_code?.length) {
            this.openInfoCard('El código de acceso es obligatorio para eventos privados. Introduce un código válido para continuar.');
          } else if (err?.status === 500 && payload.sessions) {
            // El guardado incluía cambios en las sesiones: si alguna sesión del evento
            // ya tiene entradas vendidas, el backend puede rechazar el borrado/recreado
            // con un error genérico (ver FIX_UPDATE_SESSIONS.md). Damos una pista al usuario
            // en vez de un error técnico.
            this.openInfoCard('No se han podido guardar los cambios. Puede que alguna sesión ya tenga entradas vendidas y no se pueda modificar. Inténtalo de nuevo o contacta con soporte si el problema persiste.');
          } else {
            this.openInfoCard('¡Vaya! Ha ocurrido un error al crear el evento, vuelve a intentarlo más tarde.');
          }
        }
      })
    } else {
      this.createEventService.createEventBusiness(payload).subscribe({
        next: (res) => {
          this.savingEvent = false;
          saved.values.cropped = croppedAux;
          this.idEvent = res.data.id;
          // this.event al día con lo que devuelve el back (isPublished, keepPublished e
          // inactiveEvent() leen de aquí, y en el alta getData() nunca se ejecuta).
          this.event = { ...(this.event || {}), ...res.data };
          this.afterSaveEvent(publish);
        }, error: (err) => {
          this.savingEvent = false;
          saved.values.cropped = croppedAux;

          // Mensaje específico para error de URL externa no válida
          if (err?.status === 422 && err?.error?.errors?.external_url?.length) {
            this.openInfoCard('La URL externa no es válida. Revisa que tenga el formato correcto (por ejemplo: https://www.ejemplo.com).');
          } else if (err?.status === 422 && err?.error?.errors?.private_event_code?.length) {
            this.openInfoCard('El código de acceso es obligatorio para eventos privados. Introduce un código válido para continuar.');
          } else {
            this.openInfoCard('¡Vaya! Ha ocurrido un error al crear el evento, vuelve a intentarlo más tarde.');
          }
        }
      })
    }
  }

  // Construye el bloque {sessions, start_date, end_date} del payload a partir de un
  // array de sesiones y la config de venta, de forma pura (sin leer de los servicios).
  // Se usa dos veces desde saveEvent()/stripUnchangedFields(): una con los datos en
  // vivo y otra con el snapshot original, para poder compararlos como bloque único.
  private buildSessionsPayload(sessionsSource: any[] | null | undefined, configTickets: any): { sessions: any[]; start_date: string; end_date?: string } | null {
    if (!sessionsSource || sessionsSource.length === 0) {
      return null;
    }

    // La hora de una sesión es SIEMPRE hora de pared, venga como viniere:
    //  - las temporales del alta son "2026-11-28 19:00:00" o un Date local,
    //  - las ya guardadas llegan de la API como "2026-11-28T19:00:00.000000Z",
    //    donde la Z es mentira: el backend etiqueta como UTC una hora local
    //    (ver SessionsEventComponent.getSessionDate).
    // Por eso de un string se leen los dígitos tal cual. Pasar el formato de la API
    // por formatDate lo convertía a la zona del navegador y sumaba el desfase
    // (+1h en invierno, +2h en verano) a todas las sesiones en cada guardado que
    // incluyera el bloque, además de correr el start_date/end_date del evento.
    const wallClockParts = (value: string) => value.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})/);
    const toBackendDate = (value: any) => {
      if (typeof value === 'string') {
        const parts = wallClockParts(value);
        if (parts) {
          return `${parts[1]} ${parts[2]}`;
        }
      }
      return formatDate(value, 'yyyy-MM-dd HH:mm:ss', 'es');
    };

    const cleanSessions = sessionsSource.map((element: any) => {
      const cleanSession: any = {
        id_event: element.id_event,
        id_recurring_session: element.id_recurring_session || null,
        start_date: toBackendDate(element.start_date)
      };

      // Solo incluir campos opcionales si existen
      if (element.id) cleanSession.id = element.id;
      if (element.end_date) {
        cleanSession.end_date = toBackendDate(element.end_date);
      }

      return cleanSession;
    });

    const sortedSessions = [...cleanSessions].sort((a, b) =>
      new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
    );

    const result: { sessions: any[]; start_date: string; end_date?: string } = {
      sessions: cleanSessions,
      start_date: sortedSessions[0].start_date,
    };
    if (sortedSessions.length > 1) {
      result.end_date = sortedSessions[sortedSessions.length - 1].start_date;
    }

    if (configTickets) {
      if (configTickets.typeTicketsSession === 'differentTickets') {
        result.sessions = result.sessions.map((session: any) => ({
          ...session,
          start_sales: session.start_sales ? formatDate(session.start_sales, 'yyyy-MM-dd HH:mm:ss', 'es') : null,
          end_sales: session.end_sales ? formatDate(session.end_sales, 'yyyy-MM-dd HH:mm:ss', 'es') : null
        }));
      } else {
        // El tope de entradas viaja a nivel de evento (payload.max_tickets_per_order):
        // aquí se mandaba una clave que la tabla sessions ni siquiera tiene.
        result.sessions = result.sessions.map((session: any) => ({
          ...session,
          start_sales: configTickets.start_sales ? formatDate(configTickets.start_sales, 'yyyy-MM-dd HH:mm:ss', 'es') : null,
          end_sales: configTickets.end_sales ? formatDate(configTickets.end_sales, 'yyyy-MM-dd HH:mm:ss', 'es') : null
        }));
      }
    }

    return result;
  }

  // Construye el array `tickets` del payload de forma pura (sin leer de los servicios),
  // reproduciendo el flattening de ticketsBySession y la limpieza de cada ticket. Se usa
  // dos veces (datos en vivo / snapshot original) para poder compararlos como bloque único.
  private buildTicketsPayload(ticketsSource: any[] | null | undefined, configTickets: any, isMapEvent: boolean): any[] | null {
    let tickets = ticketsSource;

    // Si estamos en modo de sesiones, obtener tickets de ticketsBySession
    if (configTickets && configTickets.ticketsBySession && configTickets.typeTicketsSession) {
      const allTickets: any[] = [];
      Object.keys(configTickets.ticketsBySession).forEach(sessionIndex => {
        const sessionTickets = configTickets.ticketsBySession[sessionIndex];
        if (sessionTickets && sessionTickets.length > 0) {
          sessionTickets.forEach((ticket: any) => {
            // Verificar si el ticket ya fue agregado (evitar duplicados)
            const exists = allTickets.find(t => t.name === ticket.name && t.id === ticket.id);
            if (!exists) {
              // Crear copia limpia del ticket sin campos innecesarios
              const cleanTicket = {
                id: ticket.id,
                name: ticket.name,
                price: ticket.price,
                active: ticket.active !== false,
                only_tpv_sales: ticket.only_tpv_sales,
                allow_discounts: ticket.allow_discounts !== false,
                levels: ticket.levels || [],
                id_session: ticket.id_session,
                base: ticket.base,
                enabled: ticket.enabled
              };
              allTickets.push(cleanTicket);
            }
          });
        }
      });
      if (allTickets.length > 0) {
        tickets = allTickets;
      }
    }

    if (!tickets) {
      return null;
    }

    const ticketsSend: any[] = [];
    tickets.forEach((element: any) => {
      // Verificar si el ticket ya existe en el array
      const existingIndex = ticketsSend.findIndex((value) => {
        return (value.id && value.id == element.id) || (value.numTickets && value.numTickets == element.numTickets);
      });

      let special_seats = element.special_seats;
      let levels: any[] = [];
      // Los tickets base configurados desde el diálogo guardan los precios en `levels`
      // (con {id, price, name, color}) mientras que los tickets cargados desde API usan
      // `ticket_levels` (con {level_id, price}). Se soportan ambas formas.
      const rawLevels = (element.ticket_levels?.length > 0 ? element.ticket_levels : null)
                      || (element.levels?.length > 0 ? element.levels : null);
      if (rawLevels && (!element.ticket_seats || element.ticket_seats.length === 0)) {
        levels = rawLevels.map((level: any) => ({
          id: level.level_id ?? level.id,
          price: level.price
        }));
      }

      let seats: any[] = [];
      let ticket_base = null;
      if (element.ticket_seats && element.ticket_seats.length > 0) {
        ticket_base = element.ticket_seats[0].id;
        special_seats = true;

        seats = (element.ticket_seats || []).map((ticket_seat: any) => ({
          id: ticket_seat.seat.id,
          price: ticket_seat.price,
        }));
      }

      const lowerPriceLevel = levels.find((level: any) => level.price < element.price);

      const cleanTicket: any = {
        // Las entradas marcadas como `base` son entradas del ESPACIO recién
        // configuradas desde esta pantalla: su id es el de la entrada base y no
        // el de una entrada de este evento. Se envían sin id para que el backend
        // las cree (si se manda el id ajeno, updateTickets no lo encuentra entre
        // las del evento y además lo cuenta como "id entrante", lo que en un
        // segundo guardado borraría la entrada creada en el primero).
        id: element.base ? null : element.id,
        name: element.name,
        price: element.price || lowerPriceLevel?.price || 0,
        active: element.active !== false,
        only_tpv_sales: element.only_tpv_sales,
        allow_discounts: element.allow_discounts !== false,
        special_seats: special_seats,
        levels: levels,
        seats: seats,
        ticket_base: ticket_base
      };

      // Omitir entradas base desactivadas explícitamente por el usuario
      if (element.base && element.enabled === false) {
        return;
      }

      // Para eventos con mapa: omitir entradas base sin precios configurados en ninguna zona
      if (isMapEvent && !cleanTicket.special_seats) {
        const hasLevelPrices = cleanTicket.levels.some(
          (l: any) => l.price !== null && l.price !== undefined && l.price !== ''
        );
        if (!hasLevelPrices) {
          return; // No enviar este ticket al backend
        }
      }

      // Solo agregar si no existe
      if (existingIndex === -1) {
        ticketsSend.push(cleanTicket);
      }
    });

    // Orden determinista para que la comparación con el snapshot original (JSON.stringify
    // en stripUnchangedFields) no dé falsos positivos por el orden de llegada.
    ticketsSend.sort((a, b) => (a.id ?? -1) - (b.id ?? -1));

    return ticketsSend;
  }

  // ¿Hay cambios sin guardar respecto a lo último cargado/guardado? Controla la
  // aparición del botón "Guardar cambios" (ver buttons-wrapper en la plantilla).
  // Reutiliza los mismos snapshots originales que stripUnchangedFields(), pero
  // comparando directamente los valores en vivo en vez de un payload ya construido.
  get hasUnsavedChanges(): boolean {
    if (!this.idEvent) {
      // Evento nuevo: aún no hay nada guardado, así que en cuanto haya al menos
      // título (mínimo exigido por saveEvent) se considera que hay algo que guardar.
      const saved = this.createEventService.getInfoEvent();
      return !!saved?.values?.title?.trim();
    }

    if (!this.originalDataCaptured || !this.originalInfoValues) {
      return false;
    }

    const eq = (a: any, b: any) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
    const sortedIds = (arr: any[]) => (arr || []).map((v: any) => v.id).sort();

    const saved = this.createEventService.getInfoEvent();
    const cur = saved?.values || {};
    const orig = this.originalInfoValues;

    const infoFieldsChanged = !eq(cur.title, orig.title)
      || !eq(cur.description, orig.description)
      || !eq(sortedIds(cur.categories), sortedIds(orig.categories))
      || !eq(cur.id_company, orig.id_company)
      // Por id las ya guardadas y por base64 las recién subidas: comparar solo `file`
      // hacía invisible quitar una foto del carrusel (todas valían undefined).
      || !eq((cur.extraImages || []).map((img: any) => img.id ?? img.file), (orig.extraImages || []).map((img: any) => img.id ?? img.file))
      // Portada y adjunto: el control no existe en el snapshot cargado por getData()
      // y su valor por defecto en el form es ''/null, así que hay que normalizar antes
      // de comparar. No vale con mirar solo si HAY archivo: tras guardar, el base64
      // sigue en el formulario (nadie lo limpia), y un "!!cur.file" a secas dejaría
      // el aviso de cambios sin guardar encendido hasta recargar la pantalla.
      || !eq(cur.file || null, orig.file || null)
      || !eq(cur.extraFile || null, orig.extraFile || null)
      || !eq(cur.cropped, orig.cropped)
      || !eq(cur.space?.id, orig.space?.id)
      || !eq(cur.space ? cur.space.address : cur.address, orig.space ? orig.space.address : orig.address)
      || !eq(cur.use_map || false, orig.use_map || false)
      || !eq(cur.hiddenEvent, orig.hiddenEvent)
      || !eq(cur.informative, orig.informative)
      || !eq(cur.external, orig.external)
      || !eq(cur.external ? cur.external_url : null, orig.external ? orig.external_url : null)
      || !eq(cur.private_event, orig.private_event)
      || !eq(cur.private_event ? cur.private_event_code : null, orig.private_event ? orig.private_event_code : null)
      || !eq(cur.only_tpv_sales, orig.only_tpv_sales)
      || !eq(cur.online, orig.online)
      || !eq(cur.invoice_business_cif, orig.invoice_business_cif)
      || !eq(cur.invoice_business_name, orig.invoice_business_name)
      || !eq(cur.invoice_business_address || null, orig.invoice_business_address || null)
      || !eq(cur.external_billing, orig.external_billing)
      || !eq(cur.fee_invoice_business_cif, orig.fee_invoice_business_cif)
      || !eq(cur.fee_invoice_business_name, orig.fee_invoice_business_name)
      || !eq(cur.external_fee_billing, orig.external_fee_billing)
      || !eq(cur.youtube_url || null, orig.youtube_url || null)
      || !eq(cur.useCustomPrice, orig.useCustomPrice)
      || !eq(cur.useCustomPrice ? cur.customFromPrice : null, orig.useCustomPrice ? orig.customFromPrice : null);

    if (infoFieldsChanged) return true;

    // El aforo solo es editable por el usuario en eventos sin mapa de butacas (ver
    // [readOnly]="map && useMapVenue" en map-seats-create.component.html); en eventos
    // con mapa, MapSeatsCreateComponent sobreescribe este observable en su propio
    // ngOnInit con venue.capacity solo para mostrarlo, sin que el usuario haya tocado nada.
    if (!cur.use_map) {
      const capacity = this.createEventService.getCapacity();
      if (!eq(capacity, this.originalCapacity)) return true;
    }

    const configTickets = this.ticketService.getTicketsConfig();
    if (!eq(configTickets, this.originalConfigTickets)) return true;

    const sessionsFromObservable = this.sessionsService.getSessionsSaved();
    const sessions = sessionsFromObservable && sessionsFromObservable.length > 0 ? sessionsFromObservable : (cur.sessions || []);
    if (!eq(sessions, orig.sessions)) return true;

    if (this.originalTickets !== undefined) {
      const tickets = this.ticketService.getTicketsSaved();
      if (!eq(tickets, this.originalTickets)) return true;
    }

    return false;
  }

  // Invocado por UnsavedEventChangesGuard al intentar salir de esta ruta (p. ej. al
  // cambiar de sección con el menú de edición). Si hay cambios sin guardar, pide
  // confirmación; si el usuario confirma, se descartan (ver discardUnsavedChanges)
  // antes de dejar continuar la navegación.
  canDeactivate(): Observable<boolean> | boolean {
    if (this.eventPublished || !this.hasUnsavedChanges) {
      return true;
    }

    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: {
        icon: true,
        warning: true,
        text: 'Tienes cambios sin guardar en esta sección. Si continúas se descartarán. ¿Quieres continuar?',
        btnCancel: true,
      },
    });

    return dialogRef.afterClosed().pipe(map((confirmed: boolean) => {
      if (confirmed) {
        this.discardUnsavedChanges();
      }
      return !!confirmed;
    }));
  }

  // Revierte los observables compartidos (info, aforo, config de venta, entradas,
  // sesiones) a los snapshots originales cargados en getData(). Los componentes hijos
  // de cada sección (app-create-event, app-sessions-event, etc.) se destruyen/recrean
  // al cambiar de :step (ver isSectionVisible), así que al volver a montarse leerán
  // estos valores restaurados como si nunca se hubiese editado nada.
  private discardUnsavedChanges() {
    if (!this.idEvent || !this.originalDataCaptured) {
      // Evento nuevo sin snapshot al que volver: no hay nada que restaurar.
      return;
    }

    this.createEventService.setInfoDetail({
      valid: true,
      values: JSON.parse(JSON.stringify(this.originalInfoValues)),
    });
    this.ticketService.setTicketsConfig(this.originalConfigTickets ? JSON.parse(JSON.stringify(this.originalConfigTickets)) : null);
    this.createEventService.setCapacity(this.originalCapacity ?? null);
    if (this.originalTickets !== undefined) {
      this.ticketService.setTicketsVenue(JSON.parse(JSON.stringify(this.originalTickets)));
    }
    this.sessionsService.clearSessions();
  }

  // Elimina del payload de edición las claves cuyo valor no cambió respecto al snapshot
  // cargado en getData() (ver originalInfoValues/originalConfigTickets/originalTickets/
  // originalCapacity). Solo se llama en modo edición. Si todavía no se ha cargado ningún
  // snapshot (p. ej. un evento recién creado que se vuelve a guardar en la misma sesión,
  // sin haber pasado por getData()), no hace nada y se manda el payload completo.
  private stripUnchangedFields(payload: any): void {
    if (!this.originalDataCaptured || !this.originalInfoValues) {
      return;
    }

    const orig = this.originalInfoValues;
    const eq = (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b);
    const sortedArr = (v: any) => Array.isArray(v) ? [...v].sort() : v;

    const scalarKeys: Array<{ key: string; original: any }> = [
      { key: 'title', original: orig.title },
      { key: 'description', original: orig.description },
      { key: 'category_ids', original: sortedArr((orig.categories || []).map((c: any) => c.id)) },
      { key: 'id_company', original: orig.id_company },
      { key: 'carousel_images', original: (orig.extraImages || []).filter((img: any) => img.file).map((img: any) => img.file) },
      { key: 'carousel_images_keep', original: (orig.extraImages || []).filter((img: any) => img.id).map((img: any) => img.id) },
      { key: 'id_space', original: orig.space?.id },
      { key: 'address', original: orig.space ? orig.space.address : orig.address },
      { key: 'id_city', original: orig.space ? orig.space.id_city : orig.id_city },
      { key: 'latitude', original: orig.space ? null : orig.latitude },
      { key: 'longitude', original: orig.space ? null : orig.longitude },
      { key: 'use_map', original: orig.use_map || false },
      { key: 'informative', original: orig.informative },
      { key: 'external', original: orig.external },
      { key: 'external_url', original: orig.external ? orig.external_url : null },
      { key: 'private_event', original: orig.private_event },
      { key: 'private_event_code', original: orig.private_event ? orig.private_event_code : null },
      { key: 'only_tpv_sales', original: orig.only_tpv_sales },
      { key: 'online', original: orig.online },
      { key: 'hidden', original: orig.hiddenEvent },
      { key: 'invoice_business_cif', original: orig.invoice_business_cif },
      { key: 'invoice_business_name', original: orig.invoice_business_name },
      { key: 'invoice_business_address', original: orig.invoice_business_address || null },
      { key: 'external_billing', original: orig.external_billing },
      { key: 'fee_invoice_business_cif', original: orig.fee_invoice_business_cif },
      { key: 'fee_invoice_business_name', original: orig.fee_invoice_business_name },
      { key: 'external_fee_billing', original: orig.external_fee_billing },
      { key: 'custom_from_price', original: orig.useCustomPrice ? orig.customFromPrice : null },
      { key: 'youtube_url', original: orig.youtube_url || null },
      { key: 'capacity', original: this.originalCapacity },
      { key: 'start_sales', original: this.originalConfigTickets?.start_sales ?? null },
      { key: 'end_sales', original: this.originalConfigTickets?.end_sales ?? null },
      { key: 'assume_fees', original: this.originalConfigTickets?.assume_fees ?? null },
      { key: 'need_validation', original: this.originalConfigTickets?.need_validation ?? null },
      { key: 'max_tickets_per_order', original: this.originalConfigTickets?.max_tickets_per_user ?? null },
      { key: 'nominative_tickets', original: !!(this.originalConfigTickets?.nominative_tickets ?? false) },
      { key: 'publish_date', original: this.originalConfigTickets?.publish ? this.originalConfigTickets?.publish_date : null },
    ];

    for (const { key, original } of scalarKeys) {
      const current = key === 'category_ids' ? sortedArr(payload[key]) : payload[key];
      if (eq(current, original)) {
        delete payload[key];
      }
    }

    // Sesiones: comparación como bloque único (nunca parcial dentro del array), usando
    // el mismo builder puro que el payload en vivo para garantizar la misma normalización.
    const baselineSessionsPayload = this.buildSessionsPayload(orig.sessions, this.originalConfigTickets);
    if (eq(payload.sessions ?? null, baselineSessionsPayload?.sessions ?? null)) {
      delete payload.sessions;
      delete payload.start_date;
      delete payload.end_date;
    }

    // Tickets: comparación como bloque único, solo si ya hay snapshot de tickets cargado
    // (getTicketsByEvent es asíncrono y podría no haber resuelto todavía al guardar).
    if (this.originalTickets !== undefined) {
      const baselineTickets = this.buildTicketsPayload(this.originalTickets, this.originalConfigTickets, !!orig.use_map);
      if (eq(payload.tickets ?? null, baselineTickets ?? null)) {
        delete payload.tickets;
      }
    }
  }

  // Eliminar / desactivar / despublicar el evento en edición. Según el estado del
  // evento la acción difiere (misma lógica que tenía el antiguo detalle de evento):
  //  - Con entradas vendidas   -> desactivar (activo:false), no se puede borrar.
  //  - Publicado sin ventas     -> despublicar (toggle-publish).
  //  - Borrador / inactivo      -> borrado real (DELETE).
  inactiveEvent() {
    if (!this.idEvent || !this.event) return;

    const hasSoldTickets = this.event.purchase && this.event.purchase > 0;

    let dialogText = '¿Estás seguro de que desea eliminar el evento?';
    if (this.event.publish && !this.event.draft && !hasSoldTickets) {
      dialogText = '¿Estás seguro de que desea despublicar el evento?';
    } else if (hasSoldTickets) {
      dialogText =
        'Este evento tiene entradas vendidas y no puede ser eliminado. ¿Desea desactivarlo?';
    }

    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: {
        icon: true,
        text: dialogText,
        warning: true,
        btnCancel: true,
      },
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (!res) return;

      if (hasSoldTickets) {
        this.apiEvents.putEventos(this.event.id, { activo: false }).subscribe({
          next: () => {
            this.event.activo = false;
            this.openInfoCard('El evento ha sido desactivado correctamente.', false, false, true);
          },
          error: () => {
            this.openInfoCard('¡Vaya! Algo ha fallado, vuelve a intentarlo más tarde.', true, false);
          },
        });
      } else if (this.event.publish && !this.event.draft) {
        this.apiEvents.togglePublishEvent(this.event.id).subscribe({
          next: () => {
            this.event.publish = false;
            this.openInfoCard('El evento ha sido despublicado correctamente.', false, false, true);
          },
          error: () => {
            this.openInfoCard('¡Vaya! Algo ha fallado, vuelve a intentarlo más tarde.', true, false);
          },
        });
      } else {
        this.apiEvents.deleteEvent(this.event.id).subscribe({
          next: () => {
            this.openInfoCard('Evento eliminado correctamente.', false, false, true);
          },
          error: () => {
            this.openInfoCard('¡Vaya! Algo ha fallado, vuelve a intentarlo más tarde.', true, false);
          },
        });
      }
    });
  }

  // Levanta el overlay de guardado, que además de informar bloquea la pantalla
  // mientras la petición está en vuelo (publicar puede tardar: el backend genera
  // las reservas de los abonados de temporada del recinto).
  private startSaving(publishing: boolean) {
    this.savingMessage = publishing
      ? 'Publicando el evento…'
      : 'Guardando el evento…';
    this.savingEvent = true;
  }

  afterSaveEvent(publish: boolean) {
    // Las sesiones se editan en un observable temporal que se vacía al guardar,
    // porque ya están en el servidor. Antes de vaciarlo hay que dejarlas en la info
    // del evento: es lo único de donde validateSteps las puede leer después, y sin
    // ellas el evento recién guardado parece no tener ninguna y el botón de publicar
    // se queda en gris pidiendo que se cree una. Se cogen de la respuesta del back,
    // no del observable, para quedarnos con las que ya tienen id.
    this.syncSavedSessionsToInfo();
    this.syncSavedImagesToInfo();
    this.sessionsService.clearSessions();

    if (publish) {
      // Publicar vacía los observables compartidos y navega a "Eventos" al cerrar el
      // diálogo. Hay que marcarlo antes: si no, canDeactivate compararía ese estado ya
      // vaciado contra los snapshots originales y siempre vería "cambios sin guardar".
      this.eventPublished = true;
      this.createEventService.setInfoDetail(null);
      this.openInfoCard('El evento se ha creado correctamente.', true, false, true);
    } else {
      this.savedEvent = true;
      this.openInfoCard('El evento se ha guardado correctamente.', false, false);
      // Antes del snapshot: la portada y el adjunto ya están en el servidor, así que
      // lo que quede de ellos en el formulario no es una subida pendiente.
      this.clearPendingUploads();
      // Actualizar los snapshots "originales" a lo que se acaba de guardar, para
      // que hasUnsavedChanges vuelva a false y el botón "Guardar cambios" se oculte.
      this.captureCurrentStateAsOriginal();
    }

    // Validar pasos después de guardar
    this.validateSteps();
  }

  // Vuelca en la info compartida las sesiones tal y como han quedado guardadas (las
  // que devuelve el back ya llevan id, así que un guardado posterior las actualiza en
  // vez de recrearlas).
  private syncSavedSessionsToInfo() {
    const sessions = this.event?.sessions;
    if (!sessions || sessions.length === 0) {
      return;
    }
    const info = this.createEventService.getInfoEvent();
    if (!info?.values) {
      return;
    }
    info.values.sessions = JSON.parse(JSON.stringify(sessions));
    this.createEventService.setInfoDetail({ ...info });
  }

  // Deja en la info compartida (y en el formulario, si está montado) las fotos del
  // carrusel tal y como han quedado guardadas: con id y sin el base64 de la subida.
  // Sin esto el formulario se queda con el base64 y el siguiente guardado las vuelve
  // a subir como fotos nuevas, que es como un evento acaba con la misma foto repetida.
  private syncSavedImagesToInfo() {
    const images = this.event?.images;
    if (!Array.isArray(images)) {
      return;
    }
    const savedImages = this.mapCarouselImages(images);

    const info = this.createEventService.getInfoEvent();
    if (info?.values) {
      info.values.extraImages = savedImages;
      this.createEventService.setInfoDetail({ ...info });
    }
    this.createEvent?.refreshExtraImages(savedImages);
  }

  // La portada y el adjunto viajan como base64 dentro del payload, y una vez guardados
  // el servidor ya los tiene. Si se quedan en el estado compartido, al volver a montarse
  // la sección el formulario los reconstruye vacíos y la comparación contra el snapshot
  // vuelve a dar "cambios sin guardar", que es justo lo que se acaba de guardar.
  private clearPendingUploads() {
    const saved = this.createEventService.getInfoEvent();
    if (saved?.values) {
      saved.values.file = '';
      saved.values.extraFile = null;
      this.createEventService.setInfoDetail({ ...saved });
    }
    // El formulario solo existe si la sección de información está montada (en edición
    // se pinta una sola sección cada vez, ver isSectionVisible).
    this.createEvent?.clearPendingUploads();
  }

  private captureCurrentStateAsOriginal() {
    const saved = this.createEventService.getInfoEvent();
    this.originalInfoValues = JSON.parse(JSON.stringify(saved?.values || {}));
    this.originalConfigTickets = JSON.parse(JSON.stringify(this.ticketService.getTicketsConfig() || null));
    this.originalCapacity = this.createEventService.getCapacity() ?? null;
    this.originalTickets = JSON.parse(JSON.stringify(this.ticketService.getTicketsSaved() || []));
    this.originalDataCaptured = true;
  }

  openInfoCard(msg: string, disabledClose: boolean = false, icon: boolean = true, close: boolean = false) {
    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: ['info-dialog'],
      disableClose: disabledClose,
      data: {
        icon: icon,
        text: msg,
        btnCancel: false
      }
    });
    if (close) {
      dialogRef.afterClosed().subscribe({
        next: (val) => {
          this.router.navigate(['/panel-control/events']);
        }
      })
    }
  }

  ngOnDestroy() {
    this.createEventService.setInfoDetail(null);
    // Limpiar sesiones temporales del observable al salir
    this.sessionsService.clearSessions();
    this.ticketService.setTicketsVenue(null);
  }
}
